/**
 * Shared plastic recovery rate table — mirrors the source of truth on the
 * backend (partyserver/src/utils/smart-scan.ts). Keep these two tables in
 * sync; the company payment phase will read the same USDC-cents convention.
 *
 * VALUE CONVENTION: values are in USDC CENTS (integer). e.g. $3.50 -> 350.
 */

export const PLASTIC_TYPES = ['PET', 'HDPE', 'nylon', 'sachet', 'mixed'] as const;
export type PlasticType = (typeof PLASTIC_TYPES)[number];

export const PLASTIC_TYPE_LABELS: Record<PlasticType, string> = {
  PET: 'PET (bottles)',
  HDPE: 'HDPE (jerry cans)',
  nylon: 'Nylon (sacks/film)',
  sachet: 'Sachet water film',
  mixed: 'Mixed / assorted',
};

/** Recovery worth per kg, in USDC cents. */
export const RATE_PER_KG_CENTS: Record<PlasticType, number> = {
  PET: 350,
  HDPE: 280,
  mixed: 120,
  nylon: 150,
  sachet: 90,
};

export function computeEstimatedValueCents(plasticType: PlasticType, estimatedKg: number): number {
  if (!Number.isFinite(estimatedKg) || estimatedKg <= 0) return 0;
  return Math.round(estimatedKg * RATE_PER_KG_CENTS[plasticType]);
}

export function formatCentsAsUsd(cents: number): string {
  return `$${(Math.max(0, cents) / 100).toFixed(2)}`;
}

export function isPlasticType(value: string): value is PlasticType {
  return (PLASTIC_TYPES as readonly string[]).includes(value);
}

export interface SmartScanResult {
  plasticType: PlasticType;
  estimatedKg: number;
  estimatedValue: number;
  confidence: number;
  note: string;
}

export const CITIES = ['Lagos', 'Abuja', 'Ibadan'] as const;
export type City = (typeof CITIES)[number];
